import { AutocompleteSelect } from '@/components/ui/AutocompleteSelect'
import { Button } from '@/components/ui/Button'
import { Fieldset } from '@/components/ui/Fieldset'
import { Input } from '@/components/ui/Input'
import { Modal } from '@/components/ui/Modal'
import { MultiSelect } from '@/components/ui/MultiSelect'
import { Select } from '@/components/ui/Select'
import { Spinner } from '@/components/ui/Spinner'
import { Textarea } from '@/components/ui/Textarea'
import { leadsApi } from '@/features/leads/api/leadsApi'
import { clientTypesApi, stagesApi, statesApi } from '@/lib/api/clientDictionariesApi'
import {
  actualitiesApi,
  buildingsApi,
  conditionsApi,
  liveComplexesApi,
  propertyTypesApi,
  repairsApi,
  roomsApi,
} from '@/lib/api/dictionariesApi'
import { citiesApi } from '@/lib/api/geolocateApi'
import { districtsApi, streetsApi, subwayApi } from '@/lib/api/streetsApi'
import { toastService } from '@/lib/toastService'
import { Checkbox, Flex, Grid, HStack, Stack, VStack } from '@chakra-ui/react'
import { useMutation, useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'
import { Controller, useForm } from 'react-hook-form'
import { useTranslation } from 'react-i18next'
import { buyersApi } from '../api/buyersApi'
import { getAmentiesOptions, getWallTypeOptions } from '../constants/buyerEnums'

/**
 * Buyer Modal Component
 * Complete form for creating/editing buyers with all fields from schema
 */
export function BuyerModal({ isOpen, onClose, buyer, onSuccess }) {
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const isEdit = !!buyer
  const [fullBuyerData, setFullBuyerData] = useState(null)
  const [isLoadingFull, setIsLoadingFull] = useState(false)

  const {
    register,
    handleSubmit,
    reset,
    control,
    formState: { errors },
  } = useForm({
    defaultValues: {
      // Basic Information
      name: '',
      phone_first: '',
      phone_second: '',
      email: '',
      commission: '',
      comment: '',

      // Organizational
      type_state: null,
      type_client: null,
      type_stage: null,
      actuality: null,
      rieltor: [],
      loyality: [],

      // Geolocation
      city: null,
      district_obj: [],
      street_obj: null,
      streets: [],
      subways: [],

      // Search Criteria
      type_building_data: [],
      room: [],
      repairs: [],

      // Property Characteristics
      rooms_from: '',
      rooms_to: '',
      floors_from: '',
      floors_to: '',
      floor: '',
      floor_to: '',
      type_building: null,
      condition: null,
      conditions: [],
      buildings: [],
      wall_type: '',

      // Areas
      area_from: '',
      area_to: '',
      total_area: '',
      area_living_room: '',
      area_kitchen: '',

      // Land/Lots
      land_from: '',
      land_to: '',
      lot_from: '',
      lot_to: '',
      apartment_count_from: '',
      apartment_count_to: '',

      // Price and Complex
      price_from: '',
      price_to: '',
      live_complex: [],
      amenties: '',

      // Dates and persons
      date_term: '',
      review_date: '',
      call_date: '',
      number_of_persons: '',

      // Client Info (private)
      client_name: '',
      client_phone: '',
      client_comment: '',
      is_public: true,
      is_cancel: false,

      // Owner Info
      name_owner: '',
      phone_owner: '',
      phone_owner_plus: '',
      email_owner: '',
    },
  })

  // Load full buyer data if buyer comes from autocomplete (has client_name but not name)
  useEffect(() => {
    const loadFullData = async () => {
      if (isOpen && buyer && buyer.id && buyer.client_name && !buyer.name) {
        // This buyer is from autocomplete, load full data
        setIsLoadingFull(true)
        try {
          const response = await buyersApi.getById(buyer.id)
          setFullBuyerData(response.data)
        } catch (error) {
          console.error('Failed to load full buyer data:', error)
          toastService.error({
            title: t('buyers.loadError'),
            description: error.message,
          })
          setFullBuyerData(null)
        } finally {
          setIsLoadingFull(false)
        }
      } else {
        setFullBuyerData(null)
      }
    }
    loadFullData()
  }, [isOpen, buyer, t])

  // Reset form when buyer changes or modal closes
  useEffect(() => {
    const buyerData = fullBuyerData || buyer

    if (isOpen && buyerData) {
      reset({
        // Basic Information
        name: buyerData.name || buyerData.client_name || '',
        phone_first: buyerData.phone_first || buyerData.client_phone || '',
        phone_second: buyerData.phone_second || '',
        email: buyerData.email || '',
        commission: buyerData.commission || '',
        comment: buyerData.comment || '',

        // Organizational
        type_state: buyerData.type_state || null,
        type_client: buyerData.type_client || null,
        type_stage: buyerData.type_stage || null,
        actuality: buyerData.actuality || null,
        rieltor: buyerData.rieltor || [],
        loyality: buyerData.loyality || [],

        // Geolocation
        city: buyerData.city || null,
        district_obj: buyerData.district_obj || [],
        street_obj: buyerData.street_obj || null,
        streets: buyerData.streets || [],
        subways: buyerData.subways || [],

        // Search Criteria
        type_building_data: buyerData.type_building_data || [],
        room: buyerData.room || [],
        repairs: buyerData.repairs || [],

        // Property Characteristics
        rooms_from: buyerData.rooms_from || '',
        rooms_to: buyerData.rooms_to || '',
        floors_from: buyerData.floors_from || '',
        floors_to: buyerData.floors_to || '',
        floor: buyerData.floor || '',
        floor_to: buyerData.floor_to || '',
        type_building: buyerData.type_building || null,
        condition: buyerData.condition || null,
        conditions: buyerData.conditions || [],
        buildings: buyerData.buildings || [],
        wall_type: buyerData.wall_type || '',

        // Areas
        area_from: buyerData.area_from || '',
        area_to: buyerData.area_to || '',
        total_area: buyerData.total_area || '',
        area_living_room: buyerData.area_living_room || '',
        area_kitchen: buyerData.area_kitchen || '',

        // Land/Lots
        land_from: buyerData.land_from || '',
        land_to: buyerData.land_to || '',
        lot_from: buyerData.lot_from || '',
        lot_to: buyerData.lot_to || '',
        apartment_count_from: buyerData.apartment_count_from || '',
        apartment_count_to: buyerData.apartment_count_to || '',

        // Price and Complex
        price_from: buyerData.price_from || '',
        price_to: buyerData.price_to || '',
        live_complex: buyerData.live_complex || [],
        amenties: buyerData.amenties || '',

        // Dates and persons
        date_term: buyerData.date_term ? buyerData.date_term.split('T')[0] : '',
        review_date: buyerData.review_date ? buyerData.review_date.split('T')[0] : '',
        call_date: buyerData.call_date ? buyerData.call_date.split('T')[0] : '',
        number_of_persons: buyerData.number_of_persons || '',

        // Client Info (private)
        client_name: buyerData.client_name || '',
        client_phone: buyerData.client_phone || '',
        client_comment: buyerData.client_comment || '',
        is_public: buyerData.is_public !== undefined ? buyerData.is_public : true,
        is_cancel: buyerData.is_cancel || false,

        // Owner Info
        name_owner: buyerData.name_owner || '',
        phone_owner: buyerData.phone_owner || '',
        phone_owner_plus: buyerData.phone_owner_plus || '',
        email_owner: buyerData.email_owner || '',
      })
    } else if (isOpen) {
      // Reset to defaults when creating new
      reset()
    }
  }, [isOpen, buyer, fullBuyerData, reset])

  // Save mutation
  const saveMutation = useMutation({
    mutationFn: async (data) => {
      // Transform data: convert numbers, extract IDs from arrays/objects, handle nulls
      const transformed = {
        // Basic Information
        name: data.name || null,
        phone_first: data.phone_first || null,
        phone_second: data.phone_second || null,
        email: data.email || null,
        commission: data.commission || null,
        comment: data.comment || null,

        // Organizational (single selects - extract ID)
        type_state: data.type_state || null,
        type_client: data.type_client || null,
        type_stage: data.type_stage || null,
        actuality: data.actuality || null,

        // Multi-selects (extract IDs from array)
        rieltor: Array.isArray(data.rieltor) ? data.rieltor.map(item =>
          typeof item === 'object' ? item.id : item
        ) : [],
        loyality: Array.isArray(data.loyality) ? data.loyality.map(item =>
          typeof item === 'object' ? item.id : item
        ) : [],

        // Geolocation
        city: data.city || null,
        district_obj: Array.isArray(data.district_obj) ? data.district_obj.map(item =>
          typeof item === 'object' ? item.id : item
        ) : [],
        street_obj: data.street_obj || null,
        streets: Array.isArray(data.streets) ? data.streets.map(item =>
          typeof item === 'object' ? item.id : item
        ) : [],
        subways: Array.isArray(data.subways) ? data.subways.map(item =>
          typeof item === 'object' ? item.id : item
        ) : [],

        // Search Criteria
        type_building_data: Array.isArray(data.type_building_data) ? data.type_building_data.map(item =>
          typeof item === 'object' ? item.id : item
        ) : [],
        room: Array.isArray(data.room) ? data.room.map(item =>
          typeof item === 'object' ? item.id : item
        ) : [],
        repairs: Array.isArray(data.repairs) ? data.repairs.map(item =>
          typeof item === 'object' ? item.id : item
        ) : [],

        // Property Characteristics (numbers)
        rooms_from: data.rooms_from ? Number(data.rooms_from) : null,
        rooms_to: data.rooms_to ? Number(data.rooms_to) : null,
        floors_from: data.floors_from ? Number(data.floors_from) : null,
        floors_to: data.floors_to ? Number(data.floors_to) : null,
        floor: data.floor ? Number(data.floor) : null,
        floor_to: data.floor_to ? Number(data.floor_to) : null,
        type_building: data.type_building || null,
        condition: data.condition || null,
        conditions: Array.isArray(data.conditions) ? data.conditions.map(item =>
          typeof item === 'object' ? item.id : item
        ) : [],
        buildings: Array.isArray(data.buildings) ? data.buildings.map(item =>
          typeof item === 'object' ? item.id : item
        ) : [],
        wall_type: data.wall_type || null,

        // Areas (numbers)
        area_from: data.area_from ? Number(data.area_from) : null,
        area_to: data.area_to ? Number(data.area_to) : null,
        total_area: data.total_area ? Number(data.total_area) : null,
        area_living_room: data.area_living_room || null,
        area_kitchen: data.area_kitchen ? Number(data.area_kitchen) : null,

        // Land/Lots (numbers)
        land_from: data.land_from ? Number(data.land_from) : null,
        land_to: data.land_to ? Number(data.land_to) : null,
        lot_from: data.lot_from ? Number(data.lot_from) : null,
        lot_to: data.lot_to ? Number(data.lot_to) : null,
        apartment_count_from: data.apartment_count_from ? Number(data.apartment_count_from) : null,
        apartment_count_to: data.apartment_count_to ? Number(data.apartment_count_to) : null,

        // Price and Complex
        price_from: data.price_from ? Number(data.price_from) : null,
        price_to: data.price_to ? Number(data.price_to) : null,
        live_complex: Array.isArray(data.live_complex) ? data.live_complex.map(item =>
          typeof item === 'object' ? item.id : item
        ) : [],
        amenties: data.amenties || null,

        // Dates
        date_term: data.date_term || null,
        review_date: data.review_date || null,
        call_date: data.call_date || null,
        number_of_persons: data.number_of_persons ? Number(data.number_of_persons) : null,

        // Client Info (private)
        client_name: data.client_name || null,
        client_phone: data.client_phone || null,
        client_comment: data.client_comment || null,
        is_public: data.is_public !== undefined ? data.is_public : true,
        is_cancel: data.is_cancel || false,

        // Owner Info
        name_owner: data.name_owner || null,
        phone_owner: data.phone_owner || null,
        phone_owner_plus: data.phone_owner_plus || null,
        email_owner: data.email_owner || null,
      }

      if (isEdit) {
        return buyersApi.update(buyer.id, transformed)
      } else {
        return buyersApi.create(transformed)
      }
    },
    onSuccess: (response) => {
      toastService.success({
        title: isEdit ? t('buyers.updateSuccess') : t('buyers.createSuccess'),
      })
      queryClient.invalidateQueries({ queryKey: ['buyers'] })
      if (onSuccess) {
        onSuccess(response.data)
      }
      handleClose()
    },
    onError: (error) => {
      toastService.error({
        title: isEdit ? t('buyers.updateError') : t('buyers.createError'),
        description: error.message,
      })
    },
  })

  const onSubmit = (data) => {
    saveMutation.mutate(data)
  }

  const handleClose = () => {
    if (!saveMutation.isPending) {
      reset()
      onClose()
    }
  }

  return (
    <Modal isOpen={isOpen} onClose={handleClose} size="xl">
      <Modal.Header>
        {isEdit ? t('buyers.editBuyer') : t('buyers.createBuyer')}
      </Modal.Header>
      <Modal.Body>
        {isLoadingFull ? (
          <Flex justify="center" align="center" minH="300px">
            <Spinner size="lg" />
          </Flex>
        ) : (
          <form id="buyer-form" onSubmit={handleSubmit(onSubmit)}>
            <VStack spacing={4} align="stretch">
              {/* Contact Information */}
              <Fieldset>
                <Fieldset.Legend>{t('buyers.contactInfo')}</Fieldset.Legend>
                <Fieldset.Content>
                  <Stack spacing={3}>
                    <Input
                      label={`${t('buyers.name')} *`}
                      placeholder={t('buyers.namePlaceholder')}
                      error={errors.name?.message}
                      {...register('name', { required: t('validation.required') })}
                    />
                    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                      <Input
                        label={t('buyers.phoneFirst')}
                        placeholder="+380XXXXXXXXX"
                        error={errors.phone_first?.message}
                        {...register('phone_first')}
                      />
                      <Input
                        label={t('buyers.phoneSecond')}
                        placeholder="+380XXXXXXXXX"
                        error={errors.phone_second?.message}
                        {...register('phone_second')}
                      />
                    </Grid>
                    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                      <Input
                        type="email"
                        label={t('buyers.email')}
                        placeholder="email@example.com"
                        error={errors.email?.message}
                        {...register('email')}
                      />
                      <Input
                        label={t('buyers.commission')}
                        placeholder="5%"
                        error={errors.commission?.message}
                        {...register('commission')}
                      />
                    </Grid>
                    <Textarea
                      label={t('buyers.comment')}
                      placeholder={t('buyers.commentPlaceholder')}
                      rows={3}
                      error={errors.comment?.message}
                      {...register('comment')}
                    />
                  </Stack>
                </Fieldset.Content>
              </Fieldset>

              {/* Organizational */}
              <Fieldset>
                <Fieldset.Legend>{t('buyers.clientInfo')}</Fieldset.Legend>
                <Fieldset.Content>
                  <Stack spacing={3}>
                    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                      <Controller
                        name="type_state"
                        control={control}
                        render={({ field }) => (
                          <AutocompleteSelect
                            label={t('buyers.typeState')}
                            placeholder={t('common.select')}
                            value={field.value}
                            onChange={field.onChange}
                            fetchOptions={async (search, page) => {
                              const response = await statesApi.getAll({ search, page })
                              return {
                                options: response.data,
                                hasMore: response.meta.pagination.has_next,
                              }
                            }}
                            error={errors.type_state?.message}
                          />
                        )}
                      />
                      <Controller
                        name="type_client"
                        control={control}
                        render={({ field }) => (
                          <AutocompleteSelect
                            label={t('buyers.typeClient')}
                            placeholder={t('common.select')}
                            value={field.value}
                            onChange={field.onChange}
                            fetchOptions={async (search, page) => {
                              const response = await clientTypesApi.getAll({ search, page })
                              return {
                                options: response.data,
                                hasMore: response.meta.pagination.has_next,
                              }
                            }}
                            error={errors.type_client?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                      <Controller
                        name="type_stage"
                        control={control}
                        render={({ field }) => (
                          <AutocompleteSelect
                            label={t('buyers.typeStage')}
                            placeholder={t('common.select')}
                            value={field.value}
                            onChange={field.onChange}
                            fetchOptions={async (search, page) => {
                              const response = await stagesApi.getAll({ search, page })
                              return {
                                options: response.data,
                                hasMore: response.meta.pagination.has_next,
                              }
                            }}
                            error={errors.type_stage?.message}
                          />
                        )}
                      />
                      <Controller
                        name="actuality"
                        control={control}
                        render={({ field }) => (
                          <AutocompleteSelect
                            label={t('buyers.actuality')}
                            placeholder={t('common.select')}
                            value={field.value}
                            onChange={field.onChange}
                            fetchOptions={async (search, page) => {
                              const response = await actualitiesApi.getAll({ search, page })
                              return {
                                options: response.data,
                                hasMore: response.meta.pagination.has_next,
                              }
                            }}
                            error={errors.actuality?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Controller
                      name="rieltor"
                      control={control}
                      render={({ field }) => (
                        <MultiSelect
                          label={t('buyers.rieltor')}
                          placeholder={t('common.selectMultiple')}
                          value={field.value}
                          onChange={field.onChange}
                          fetchOptions={async (search, page) => {
                            const response = await leadsApi.getRealtors({ search, page })
                            return {
                              options: response.data,
                              hasMore: response.meta.pagination.has_next,
                            }
                          }}
                          error={errors.rieltor?.message}
                        />
                      )}
                    />
                    <Controller
                      name="loyality"
                      control={control}
                      render={({ field }) => (
                        <MultiSelect
                          label={t('buyers.loyality')}
                          placeholder={t('common.selectMultiple')}
                          value={field.value}
                          onChange={field.onChange}
                          fetchOptions={async (search, page) => {
                            const response = await leadsApi.getLoyalties({ search, page })
                            return {
                              options: response.data,
                              hasMore: response.meta.pagination.has_next,
                            }
                          }}
                          error={errors.loyality?.message}
                        />
                      )}
                    />
                  </Stack>
                </Fieldset.Content>
              </Fieldset>

              {/* Geolocation */}
              <Fieldset>
                <Fieldset.Legend>{t('buyers.geolocation')}</Fieldset.Legend>
                <Fieldset.Content>
                  <Stack spacing={3}>
                    <Controller
                      name="city"
                      control={control}
                      render={({ field }) => (
                        <AutocompleteSelect
                          label={t('buyers.city')}
                          placeholder={t('common.select')}
                          value={field.value}
                          onChange={field.onChange}
                          fetchOptions={async (search, page) => {
                            const response = await citiesApi.getAutocomplete({ search, page })
                            return {
                              options: response.data,
                              hasMore: response.meta.pagination.has_next,
                            }
                          }}
                          error={errors.city?.message}
                        />
                      )}
                    />
                    <Controller
                      name="district_obj"
                      control={control}
                      render={({ field }) => (
                        <MultiSelect
                          label={t('buyers.districtObj')}
                          placeholder={t('common.selectMultiple')}
                          value={field.value}
                          onChange={field.onChange}
                          fetchOptions={async (search, page) => {
                            const response = await districtsApi.getAll({ search, page })
                            return {
                              options: response.data,
                              hasMore: response.meta.pagination.has_next,
                            }
                          }}
                          error={errors.district_obj?.message}
                        />
                      )}
                    />
                    <Controller
                      name="street_obj"
                      control={control}
                      render={({ field }) => (
                        <AutocompleteSelect
                          label={t('buyers.streetObj')}
                          placeholder={t('common.select')}
                          value={field.value}
                          onChange={field.onChange}
                          fetchOptions={async (search, page) => {
                            const response = await streetsApi.getAutocomplete({ search, page })
                            return {
                              options: response.data,
                              hasMore: response.meta.pagination.has_next,
                            }
                          }}
                          error={errors.street_obj?.message}
                        />
                      )}
                    />
                    <Controller
                      name="streets"
                      control={control}
                      render={({ field }) => (
                        <MultiSelect
                          label={t('buyers.streets')}
                          placeholder={t('common.selectMultiple')}
                          value={field.value}
                          onChange={field.onChange}
                          fetchOptions={async (search, page) => {
                            const response = await streetsApi.getAll({ search, page })
                            return {
                              options: response.data,
                              hasMore: response.meta.pagination.has_next,
                            }
                          }}
                          error={errors.streets?.message}
                        />
                      )}
                    />
                    <Controller
                      name="subways"
                      control={control}
                      render={({ field }) => (
                        <MultiSelect
                          label={t('buyers.subways')}
                          placeholder={t('common.selectMultiple')}
                          value={field.value}
                          onChange={field.onChange}
                          fetchOptions={async (search, page) => {
                            const response = await subwayApi.getAll({ search, page })
                            return {
                              options: response.data,
                              hasMore: response.meta.pagination.has_next,
                            }
                          }}
                          error={errors.subways?.message}
                        />
                      )}
                    />
                  </Stack>
                </Fieldset.Content>
              </Fieldset>

              {/* Search Criteria */}
              <Fieldset>
                <Fieldset.Legend>{t('buyers.searchCriteria')}</Fieldset.Legend>
                <Fieldset.Content>
                  <Stack spacing={3}>
                    <Controller
                      name="type_building_data"
                      control={control}
                      rules={{ required: t('validation.required') }}
                      render={({ field }) => (
                        <MultiSelect
                          label={`${t('buyers.typeBuildingData')} *`}
                          placeholder={t('common.selectMultiple')}
                          value={field.value}
                          onChange={field.onChange}
                          fetchOptions={async (search, page) => {
                            const response = await propertyTypesApi.getAll({ search, page })
                            return {
                              options: response.data,
                              hasMore: response.meta.pagination.has_next,
                            }
                          }}
                          error={errors.type_building_data?.message}
                        />
                      )}
                    />
                    <Controller
                      name="room"
                      control={control}
                      rules={{ required: t('validation.required') }}
                      render={({ field }) => (
                        <MultiSelect
                          label={`${t('buyers.room')} *`}
                          placeholder={t('common.selectMultiple')}
                          value={field.value}
                          onChange={field.onChange}
                          fetchOptions={async (search, page) => {
                            const response = await roomsApi.getAll({ search, page })
                            return {
                              options: response.data,
                              hasMore: response.meta.pagination.has_next,
                            }
                          }}
                          error={errors.room?.message}
                        />
                      )}
                    />
                    <Controller
                      name="repairs"
                      control={control}
                      rules={{ required: t('validation.required') }}
                      render={({ field }) => (
                        <MultiSelect
                          label={`${t('buyers.repairs')} *`}
                          placeholder={t('common.selectMultiple')}
                          value={field.value}
                          onChange={field.onChange}
                          fetchOptions={async (search, page) => {
                            const response = await repairsApi.getAll({ search, page })
                            return {
                              options: response.data,
                              hasMore: response.meta.pagination.has_next,
                            }
                          }}
                          error={errors.repairs?.message}
                        />
                      )}
                    />
                  </Stack>
                </Fieldset.Content>
              </Fieldset>

              {/* Property Requirements */}
              <Fieldset>
                <Fieldset.Legend>{t('buyers.propertyRequirements')}</Fieldset.Legend>
                <Fieldset.Content>
                  <Stack spacing={3}>
                    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                      <Input
                        type="number"
                        label={t('buyers.roomsFrom')}
                        placeholder="1"
                        error={errors.rooms_from?.message}
                        {...register('rooms_from')}
                      />
                      <Input
                        type="number"
                        label={t('buyers.roomsTo')}
                        placeholder="3"
                        error={errors.rooms_to?.message}
                        {...register('rooms_to')}
                      />
                    </Grid>
                    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                      <Input
                        type="number"
                        label={t('buyers.floorsFrom')}
                        placeholder="1"
                        error={errors.floors_from?.message}
                        {...register('floors_from')}
                      />
                      <Input
                        type="number"
                        label={t('buyers.floorsTo')}
                        placeholder="10"
                        error={errors.floors_to?.message}
                        {...register('floors_to')}
                      />
                    </Grid>
                    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                      <Input
                        type="number"
                        label={t('buyers.floor')}
                        placeholder="5"
                        error={errors.floor?.message}
                        {...register('floor')}
                      />
                      <Input
                        type="number"
                        label={t('buyers.floorTo')}
                        placeholder="9"
                        error={errors.floor_to?.message}
                        {...register('floor_to')}
                      />
                    </Grid>
                    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                      <Controller
                        name="type_building"
                        control={control}
                        render={({ field }) => (
                          <AutocompleteSelect
                            label={t('buyers.typeBuilding')}
                            placeholder={t('common.select')}
                            value={field.value}
                            onChange={field.onChange}
                            fetchOptions={async (search, page) => {
                              const response = await buildingsApi.getAll({ search, page })
                              return {
                                options: response.data,
                                hasMore: response.meta.pagination.has_next,
                              }
                            }}
                            error={errors.type_building?.message}
                          />
                        )}
                      />
                      <Controller
                        name="condition"
                        control={control}
                        render={({ field }) => (
                          <AutocompleteSelect
                            label={t('buyers.condition')}
                            placeholder={t('common.select')}
                            value={field.value}
                            onChange={field.onChange}
                            fetchOptions={async (search, page) => {
                              const response = await conditionsApi.getAll({ search, page })
                              return {
                                options: response.data,
                                hasMore: response.meta.pagination.has_next,
                              }
                            }}
                            error={errors.condition?.message}
                          />
                        )}
                      />
                    </Grid>
                    <Controller
                      name="conditions"
                      control={control}
                      render={({ field }) => (
                        <MultiSelect
                          label={t('buyers.conditions')}
                          placeholder={t('common.selectMultiple')}
                          value={field.value}
                          onChange={field.onChange}
                          fetchOptions={async (search, page) => {
                            const response = await conditionsApi.getAll({ search, page })
                            return {
                              options: response.data,
                              hasMore: response.meta.pagination.has_next,
                            }
                          }}
                          error={errors.conditions?.message}
                        />
                      )}
                    />
                    <Controller
                      name="buildings"
                      control={control}
                      render={({ field }) => (
                        <MultiSelect
                          label={t('buyers.buildings')}
                          placeholder={t('common.selectMultiple')}
                          value={field.value}
                          onChange={field.onChange}
                          fetchOptions={async (search, page) => {
                            const response = await buildingsApi.getAll({ search, page })
                            return {
                              options: response.data,
                              hasMore: response.meta.pagination.has_next,
                            }
                          }}
                          error={errors.buildings?.message}
                        />
                      )}
                    />
                    <Select
                      label={t('buyers.wallType')}
                      placeholder={t('common.select')}
                      error={errors.wall_type?.message}
                      {...register('wall_type')}
                    >
                      {getWallTypeOptions().map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </Stack>
                </Fieldset.Content>
              </Fieldset>

              {/* Area Information */}
              <Fieldset>
                <Fieldset.Legend>{t('buyers.areaInfo')}</Fieldset.Legend>
                <Fieldset.Content>
                  <Stack spacing={3}>
                    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                      <Input
                        type="number"
                        label={t('buyers.areaFrom')}
                        placeholder="50"
                        error={errors.area_from?.message}
                        {...register('area_from')}
                      />
                      <Input
                        type="number"
                        label={t('buyers.areaTo')}
                        placeholder="100"
                        error={errors.area_to?.message}
                        {...register('area_to')}
                      />
                    </Grid>
                    <Grid templateColumns="repeat(3, 1fr)" gap={3}>
                      <Input
                        type="number"
                        label={t('buyers.totalArea')}
                        placeholder="75"
                        error={errors.total_area?.message}
                        {...register('total_area')}
                      />
                      <Input
                        label={t('buyers.areaLivingRoom')}
                        placeholder="50"
                        error={errors.area_living_room?.message}
                        {...register('area_living_room')}
                      />
                      <Input
                        type="number"
                        label={t('buyers.areaKitchen')}
                        placeholder="12"
                        error={errors.area_kitchen?.message}
                        {...register('area_kitchen')}
                      />
                    </Grid>
                  </Stack>
                </Fieldset.Content>
              </Fieldset>

              {/* Land/Lots */}
              <Fieldset>
                <Fieldset.Legend>{t('buyers.landInfo')}</Fieldset.Legend>
                <Fieldset.Content>
                  <Stack spacing={3}>
                    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                      <Input
                        type="number"
                        label={t('buyers.landFrom')}
                        placeholder="5"
                        error={errors.land_from?.message}
                        {...register('land_from')}
                      />
                      <Input
                        type="number"
                        label={t('buyers.landTo')}
                        placeholder="10"
                        error={errors.land_to?.message}
                        {...register('land_to')}
                      />
                    </Grid>
                    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                      <Input
                        type="number"
                        label={t('buyers.lotFrom')}
                        placeholder="100"
                        error={errors.lot_from?.message}
                        {...register('lot_from')}
                      />
                      <Input
                        type="number"
                        label={t('buyers.lotTo')}
                        placeholder="200"
                        error={errors.lot_to?.message}
                        {...register('lot_to')}
                      />
                    </Grid>
                    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                      <Input
                        type="number"
                        label={t('buyers.apartmentCountFrom')}
                        placeholder="50"
                        error={errors.apartment_count_from?.message}
                        {...register('apartment_count_from')}
                      />
                      <Input
                        type="number"
                        label={t('buyers.apartmentCountTo')}
                        placeholder="100"
                        error={errors.apartment_count_to?.message}
                        {...register('apartment_count_to')}
                      />
                    </Grid>
                  </Stack>
                </Fieldset.Content>
              </Fieldset>

              {/* Price and Complex */}
              <Fieldset>
                <Fieldset.Legend>{t('buyers.priceAndComplex')}</Fieldset.Legend>
                <Fieldset.Content>
                  <Stack spacing={3}>
                    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                      <Input
                        type="number"
                        label={t('buyers.priceFrom')}
                        placeholder="50000"
                        error={errors.price_from?.message}
                        {...register('price_from')}
                      />
                      <Input
                        type="number"
                        label={t('buyers.priceTo')}
                        placeholder="100000"
                        error={errors.price_to?.message}
                        {...register('price_to')}
                      />
                    </Grid>
                    <Controller
                      name="live_complex"
                      control={control}
                      render={({ field }) => (
                        <MultiSelect
                          label={t('buyers.liveComplex')}
                          placeholder={t('common.selectMultiple')}
                          value={field.value}
                          onChange={field.onChange}
                          fetchOptions={async (search, page) => {
                            const response = await liveComplexesApi.getAutocomplete({ search, page })
                            return {
                              options: response.data,
                              hasMore: response.meta.pagination.has_next,
                            }
                          }}
                          error={errors.live_complex?.message}
                        />
                      )}
                    />
                    <Select
                      label={t('buyers.amenties')}
                      placeholder={t('common.select')}
                      error={errors.amenties?.message}
                      {...register('amenties')}
                    >
                      {getAmentiesOptions().map((option) => (
                        <option key={option.value} value={option.value}>
                          {option.label}
                        </option>
                      ))}
                    </Select>
                  </Stack>
                </Fieldset.Content>
              </Fieldset>

              {/* Dates and Persons */}
              <Fieldset>
                <Fieldset.Legend>{t('buyers.datesAndPersons')}</Fieldset.Legend>
                <Fieldset.Content>
                  <Stack spacing={3}>
                    <Grid templateColumns="repeat(3, 1fr)" gap={3}>
                      <Input
                        type="date"
                        label={t('buyers.dateTerm')}
                        error={errors.date_term?.message}
                        {...register('date_term')}
                      />
                      <Input
                        type="date"
                        label={t('buyers.reviewDate')}
                        error={errors.review_date?.message}
                        {...register('review_date')}
                      />
                      <Input
                        type="date"
                        label={t('buyers.callDate')}
                        error={errors.call_date?.message}
                        {...register('call_date')}
                      />
                    </Grid>
                    <Input
                      type="number"
                      label={t('buyers.numberOfPersons')}
                      placeholder="1"
                      error={errors.number_of_persons?.message}
                      {...register('number_of_persons')}
                    />
                  </Stack>
                </Fieldset.Content>
              </Fieldset>

              {/* Client Info (Private) */}
              <Fieldset>
                <Fieldset.Legend>{t('buyers.clientInfoPrivate')}</Fieldset.Legend>
                <Fieldset.Content>
                  <Stack spacing={3}>
                    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                      <Input
                        label={t('buyers.clientName')}
                        placeholder={t('buyers.namePlaceholder')}
                        error={errors.client_name?.message}
                        {...register('client_name')}
                      />
                      <Input
                        label={t('buyers.clientPhone')}
                        placeholder="+380XXXXXXXXX"
                        error={errors.client_phone?.message}
                        {...register('client_phone')}
                      />
                    </Grid>
                    <Textarea
                      label={t('buyers.clientComment')}
                      placeholder={t('buyers.commentPlaceholder')}
                      rows={2}
                      error={errors.client_comment?.message}
                      {...register('client_comment')}
                    />
                    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                      <Checkbox {...register('is_public')}>
                        {t('buyers.isPublic')}
                      </Checkbox>
                      <Checkbox {...register('is_cancel')}>
                        {t('buyers.isCancel')}
                      </Checkbox>
                    </Grid>
                  </Stack>
                </Fieldset.Content>
              </Fieldset>

              {/* Owner Info */}
              <Fieldset>
                <Fieldset.Legend>{t('buyers.ownerInfo')}</Fieldset.Legend>
                <Fieldset.Content>
                  <Stack spacing={3}>
                    <Input
                      label={t('buyers.nameOwner')}
                      placeholder={t('buyers.namePlaceholder')}
                      error={errors.name_owner?.message}
                      {...register('name_owner')}
                    />
                    <Grid templateColumns="repeat(2, 1fr)" gap={3}>
                      <Input
                        label={t('buyers.phoneOwner')}
                        placeholder="+380XXXXXXXXX"
                        error={errors.phone_owner?.message}
                        {...register('phone_owner')}
                      />
                      <Input
                        label={t('buyers.phoneOwnerPlus')}
                        placeholder="+380XXXXXXXXX"
                        error={errors.phone_owner_plus?.message}
                        {...register('phone_owner_plus')}
                      />
                    </Grid>
                    <Input
                      type="email"
                      label={t('buyers.emailOwner')}
                      placeholder="owner@example.com"
                      error={errors.email_owner?.message}
                      {...register('email_owner')}
                    />
                  </Stack>
                </Fieldset.Content>
              </Fieldset>
            </VStack>
          </form>
        )}
      </Modal.Body>
      <Modal.Footer>
        <HStack justify="flex-end" spacing={ 3 }>
          <Button
            variant="outline"
            onClick={handleClose}
            isDisabled={saveMutation.isPending}
          >
            {t('common.cancel')}
          </Button>
          <Button
            type="submit"
            form="buyer-form"
            isLoading={saveMutation.isPending}
          >
            {isEdit ? t('common.save') : t('common.create')}
          </Button>
        </HStack>
      </Modal.Footer>
    </Modal>
  )
}

export default BuyerModal
